/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable max-lines */

import { deleteCustomerContract, upsertCustomerContract } from 'actions/customerContracts';
import {
  platformMap,
  scanMap,
} from 'components/CustomerImport/ImportReviewSelections/ImportReviewDetails';
import LoadingIndicator from 'components/LoadingIndicator';
import Modal from 'components/Modal';
import { CLOSE_ICON } from 'constants/envelopeIcons';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getFileUploadsUploading } from 'selectors/fileUploads';
import { useSelector } from 'store';
import { CustomerContract } from 'types';

export const lenghtOfWorkOptions = [
  {
    value: '',
    label: 'Length of work',
  },
  {
    value: '3',
    label: '3 months',
  },
  {
    value: '6',
    label: '6 months',
  },
  {
    value: '12',
    label: '12 months',
  },
];

type ComponentProps = {
  contract: CustomerContract;
  isModalOpen: boolean;
  toggleModalOpen: () => void;
};

const CreateCustomerContractModal: React.FC<ComponentProps> = ({
  contract,
  isModalOpen,
  toggleModalOpen,
}) => {
  const dispatch = useDispatch();

  const uploadLoading = useSelector(getFileUploadsUploading);

  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>(contract.contract_filename || '');
  const [uploading, setUploading] = useState(false);

  const { register, getValues, handleSubmit } = useForm<{
    notes: string;
  }>({
    defaultValues: {
      notes: contract.notes || '',
    },
  });

  useEffect(() => {
    if (uploadLoading) {
      setUploading(true);
    }
  }, [uploadLoading]);

  useEffect(() => {
    if (!uploadLoading && uploading) {
      setUploading(false);
      toggleModalOpen();
    }
  }, [uploadLoading, uploading, toggleModalOpen]);

  const onSubmit = (): void => {
    const { notes } = getValues();
    if (file) {
      // add file
      dispatch(
        upsertCustomerContract({
          contract: {
            ...contract,
            uuid: contract.uuid,
            contract_filename: file.name,
            notes,
            file,
          },
          action: 'add',
        })
      );
    } else {
      if ((fileName && contract.contract_filename) || (!fileName && !contract.contract_filename)) {
        // don't touch file
        dispatch(
          upsertCustomerContract({
            contract: {
              uuid: contract.uuid,
              notes,
            },
          })
        );
        return;
      }

      if (!fileName && contract.contract_filename) {
        // delete file
        dispatch(
          upsertCustomerContract({
            contract: {
              uuid: contract.uuid,
              notes,
            },
            action: 'delete',
          })
        );
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files != null) {
      setFile(event.target.files[0]);
      setFileName(event.target.files[0].name);
    }
  };

  const handleSaveButton = (): void => {
    handleSubmit(onSubmit)();
  };

  const handleRemoveFile = (): void => {
    setFile(null);
    setFileName('');
  };

  const handleDeleteContract = (): void => {
    dispatch(deleteCustomerContract({ uuid: contract.uuid }));
    toggleModalOpen();
  };

  const renderCreateReviewSet = (
    <div className="w-full flex flex-row pt-4 pb-10 text-body">
      <form className="w-full h-full flex flex-row gap-2">
        <div className="pr-20 w-1/2 flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <p>
              {contract.end_date ? 'Lookback date range' : 'Livestream start date'}:{' '}
              <b>{moment(contract.start_date).format('MM/DD/yyyy')}</b>
              {contract.end_date && <b>–{moment(contract.end_date).format('MM/DD/yyyy')}</b>}
            </p>
            <p>
              Platform permissions needed:{' '}
              <b>
                {contract.platforms
                  ?.map((p) => platformMap[p as keyof typeof platformMap])
                  .join(', ') || 'None'}
              </b>
            </p>
            <p>
              Scan attachments for{' '}
              <b>{scanMap[contract.scan_for as keyof typeof scanMap] || 'none'}</b>
            </p>
          </div>

          <div className="flex flex-row gap-4 items-center">
            {fileName && (
              <div className="flex flex-row">
                <button type="button" className="focus:outline-none" onClick={handleRemoveFile}>
                  {CLOSE_ICON}
                </button>

                <span className="ml-2 max-w-104 truncate underline">{fileName.split('.')[0]}</span>
                <span className="underline">.{fileName.split('.').pop()}</span>
              </div>
            )}

            <label
              htmlFor="file-input"
              className="self-end w-36 h-8 inline-flex justify-center items-center px-4 py-2 text-body rounded bg-white border border-litlingo-gray-3 cursor-pointer"
            >
              {uploadLoading ? (
                <LoadingIndicator size="5" />
              ) : (
                <span className="text-litlingo-gray-5 font-bold">Add SOW</span>
              )}

              <input id="file-input" type="file" className="hidden" onChange={handleInputChange} />
            </label>
          </div>
        </div>

        <div className="flex items-stretch border-r border-litlingo-gray-3" />

        <div className="w-1/2 flex">
          <div className="w-88 mx-auto flex flex-col gap-1">
            <span>Notes</span>

            <textarea
              id="notes"
              ref={register()}
              name="notes"
              rows={7}
              className="form-input w-full p-2 text-body rounded-md resize-none placeholder-italic placeholder-litlingo-gray-6 border-litlingo-gray-2"
            />
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          body={renderCreateReviewSet}
          title="Service of Work"
          okButton
          okButtonText="Save"
          okButtonOnClick={handleSaveButton}
          toggleShowModal={toggleModalOpen}
          secondaryButton
          secondaryButtonStyle="text-litlingo-alert underline focus:outline-none"
          secondaryButtonText="Delete service of work"
          secondaryButtonOnclick={handleDeleteContract}
          style={{ width: '65rem' }}
        />
      )}
    </>
  );
};

export default CreateCustomerContractModal;
